// RemindersStep.tsx — the last step of onboarding (ONBOARDING_2026, native only):
// the invitation to be reminded to come back.
//
// Reminders were deliberately taken OUT of onboarding once before, because the old
// flow asked for a notification permission in the first thirty seconds, before the
// app had earned anything. This is not that. It comes last, after consent, after the
// user has chosen to be here, and it explains what the reminder is FOR before asking:
// not "don't break your streak" but "some days you will need this and won't think to
// open it".
//
// Two beats, in the order the system prompt makes sense in:
//   1  invite  the reason, then a single "Yes please" that asks for permission
//   2  time    only once permission is granted, choose when
// Asking for the time first and then having the permission refused would leave the
// user having made a choice the app then cannot honour.
//
// Declining is a first-class answer: "Not just now" leaves the reminder unset, and the
// Today screen's own invitation is still there whenever they change their mind.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { requestNotificationPermission } from '@/services/reminders';

/** Sensible starting points, so most people never open the time field at all. */
const PRESETS: { label: string; time: string }[] = [
  { label: 'Morning', time: '08:00' },
  { label: 'Afternoon', time: '13:00' },
  { label: 'Evening', time: '19:00' },
];

const REASONS: { icon: 'heart' | 'shield' | 'leaf'; text: React.ReactNode }[] = [
  {
    icon: 'shield',
    text: (
      <>
        <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>On the hard days</strong>, when your
        ex has been in touch or something has knocked you sideways, it is difficult to remember that
        there is anything that helps. A reminder puts it back in front of you.
      </>
    ),
  },
  {
    icon: 'leaf',
    text: (
      <>
        <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>On the ordinary days</strong>, the
        slower work of recovering happens in small pieces. A few minutes, often, does more than an
        afternoon once a month.
      </>
    ),
  },
  {
    icon: 'heart',
    text: (
      <>
        It is a nudge, not a demand.{' '}
        <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>Nothing is counted</strong>, nothing
        is missed, and you can change the time or turn it off whenever you like.
      </>
    ),
  },
];

export function RemindersStep({
  onDone,
  onSet,
}: {
  /** Skipped, or the permission was refused: leave the reminder unset and go on. */
  onDone: () => void;
  /** A time was chosen ("HH:MM"). The caller schedules it and moves on. */
  onSet: (time: string) => void;
}) {
  const [beat, setBeat] = React.useState<'invite' | 'time'>('invite');
  const [time, setTime] = React.useState('08:00');
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState(false);

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    const res = await requestNotificationPermission();
    setBusy(false);
    // 'unsupported' cannot happen here (this step is native-only) but is treated the
    // same way it would have to be: no promise we cannot keep.
    if (res === 'granted') { setBeat('time'); return; }
    setRefused(true);
  };

  // Permission refused at the system prompt. Say so plainly, say where to change it,
  // and do not ask again or leave them stuck on a step they cannot complete.
  if (refused) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Display text="That is completely fine." size={26} weight={400} lh={1.18} style={{ marginBottom: 12 }} />
        <Body size={15} style={{ lineHeight: 1.65, color: 'var(--ink-soft)', maxWidth: '34ch' }}>
          Notifications are switched off for ReNurture, so we will not be able to send you anything.
          You can turn them on in your phone’s settings at any time, and set a reminder from the You
          tab whenever you want one.
        </Body>
        <Btn full size="lg" onClick={onDone} style={{ marginTop: 26 }}>Continue</Btn>
      </div>
    );
  }

  if (beat === 'time') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Display text="When would you like it?" size={26} weight={400} lh={1.18} style={{ marginBottom: 10 }} />
        <Body size={15} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 20, maxWidth: '34ch' }}>
          Pick a time you are usually able to stop for a minute. You can change it whenever you like.
        </Body>

        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 16 }}>
          {PRESETS.map((p) => {
            const on = time === p.time;
            return (
              <button
                key={p.time}
                type="button"
                onClick={() => setTime(p.time)}
                aria-pressed={on}
                style={{
                  padding: '10px 18px', borderRadius: 999, cursor: 'pointer',
                  border: on ? '1px solid var(--green-deep)' : '1px solid var(--hair-strong)',
                  background: on ? 'rgba(29,158,117,0.10)' : 'var(--card-top)',
                  color: on ? 'var(--green-deep)' : 'var(--ink)',
                  fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600,
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Body size={14} style={{ color: 'var(--ink-soft)' }}>or a time that suits you</Body>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value || '08:00')}
            aria-label="Reminder time"
            style={{
              padding: '10px 13px', borderRadius: 11, border: '1px solid var(--hair-strong)',
              background: 'var(--card-top)', color: 'var(--ink)',
              fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15,
            }}
          />
        </label>

        <Btn full size="lg" onClick={() => onSet(time)}>Set my reminder</Btn>
        <Btn full size="lg" variant="ghost" onClick={onDone} style={{ marginTop: 8 }}>Not just now</Btn>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div
        aria-hidden="true"
        style={{
          width: 54, height: 54, borderRadius: 999, marginBottom: 16,
          background: 'rgba(200,134,26,0.13)', color: 'var(--amber-deep)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="clock" size={25} />
      </div>

      <Display text="Shall we remind you to come back?" size={26} weight={400} lh={1.2} style={{ marginBottom: 12 }} />
      <Body size={15} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 20, maxWidth: '34ch' }}>
        A quiet daily nudge, at a time you choose. The people who get the most from ReNurture are
        usually the ones who are reminded it is here.
      </Body>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginBottom: 26 }}>
        {REASONS.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 2, color: 'var(--amber-deep)' }}>
              <Icon name={r.icon} size={17} />
            </span>
            <Body size={14} style={{ lineHeight: 1.55, color: 'var(--ink-soft)' }}>{r.text}</Body>
          </div>
        ))}
      </div>

      <Btn full size="lg" onClick={ask} disabled={busy}>
        {busy ? 'One moment…' : 'Yes please, remind me'}
      </Btn>
      <Btn full size="lg" variant="ghost" onClick={onDone} style={{ marginTop: 8 }}>Not just now</Btn>
    </div>
  );
}
